import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom'; // eslint-disable-line no-restricted-imports

import { timeoutFetchSentryGuard } from 'Fetch';
import { getValuationStatus } from 'PrivateApis';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { assignOptimizelyWebData } from 'Utilities/analytics/optimizely/optimizelyWebData';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { HERO_ANIMATION_STATES } from 'Utilities/animation';
import { stringNumberToInt } from 'Utilities/formatter';
import { checkHighMileage } from 'Utilities/formValidators';
import { getMileage } from 'Utilities/mileage';
import { isValuationRestrictedToModify } from 'Utilities/restricted';

import { HeroMileageProps } from '../../common/Hero/HeroInput/SubComponents/HeroInputMileageWrapper/HeroInputMileageWrapper';
import { useFinanceExperiment } from '../../experiments/useFinanceExperiment/useFinanceExperiment';

export const useHeroMileageInputHelpers = ({
  isCarValueTracker, isFinanceEnabled = false, loadingHandler, onSubmit, setShowMileage,
}: Omit<HeroMileageProps, 'isHomePageRedesign' | 'showMileage'> & { isFinanceEnabled?: boolean }) => {
  const { vehicleLookupActions, vehicleLookupState: lookupVehicle } = useContext(VehicleLookupContext);
  const { isFinanceExperimentOn } = useFinanceExperiment();
  const isFinanceExperimentOnRef = useRef(isFinanceExperimentOn);
  isFinanceExperimentOnRef.current = isFinanceExperimentOn;
  const [confirmedMileage, setConfirmedMileage] = useState<{mileage: number} | null>();
  const [enquiryStatus, setEnquiryStatus] = useState();
  const enquiryID = lookupVehicle?.enquiry?.dpEnquiryId;
  const isRestricted = isValuationRestrictedToModify(enquiryStatus);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();

  const vrm = lookupVehicle?.vrm;

  const updateMileage = useCallback(async ({ mileage }: { mileage: number }) => {
    const newMileage = stringNumberToInt(mileage);
    const currentMileage = getMileage({ vehicle: lookupVehicle });

    const updatePayload = {
      enquiry: {
        mileage: newMileage,
      },
    };

    // New mileage means new enquiry so nuke the enquiry ID to force a new enquiry
    // But if vehicle has restricted status, don't modify existing enquiry
    if (newMileage !== currentMileage && (!isRestricted)) {
      // @ts-expect-error
      updatePayload.enquiry.id = null;
    }

    // User is confirming their mileage by pressing continue,
    // So update enquiry so they don't have to fill it in at the next step
    vehicleLookupActions.update(updatePayload);

    OPTIMIZELY_TRACK(EVENT.VALUATIONS_MILEAGE_CTA_CLICKED);

    if (!isCarValueTracker) {
      VALUATIONS_GA_TAGS.HERO_MILEAGE_SUBMITTED({ mileage, vrm });
      OPTIMIZELY_TRACK(EVENT.HERO_MILEAGE_CTA_CLICKED);
      OPTIMIZELY_TRACK(EVENT.SMV_MILEAGE_CTA_CLICKED);

      if (confirmedMileage) {
        VALUATIONS_GA_TAGS.CONFIRM_MILEAGE_MODAL_SUBMITTED({ mileage, vrm });
      }
    }

    loadingHandler((isFinanceEnabled && isFinanceExperimentOnRef.current)
      ? HERO_ANIMATION_STATES.finance : HERO_ANIMATION_STATES.mileage);
    onSubmit?.(newMileage);
  }, [confirmedMileage, isFinanceEnabled]);

  const onNotMyCarClick = useCallback(() => {
    VALUATIONS_GA_TAGS.HERO_MILEAGE_NOT_MY_CAR_CLICKED();

    if (!isCarValueTracker) {
      VALUATIONS_GA_TAGS.HERO_MILEAGE_NOT_MY_CAR_CLICKED_SNOWPLOW(vrm);
    }
    setShowMileage?.(false);
    vehicleLookupActions.remove();
  }, []);

  const onFormSubmit = useCallback(({ mileage }: { mileage: number }) => {
    // @ts-ignore
    if (checkHighMileage(true, lookupVehicle)(mileage)) {
      setConfirmedMileage({ mileage });

      if (!isCarValueTracker) {
        VALUATIONS_GA_TAGS.CONFIRM_MILEAGE_MODAL({ mileage, vrm });
      }

      return;
    }
    flushSync(() => {
      updateMileage({ mileage });
    });
  }, [lookupVehicle, updateMileage, vrm]);

  const confirmMileageHelpers = useCallback(() => ({
    onConfirm: (value: { mileage: number }) => updateMileage(value),
    onReject: () => {
      setConfirmedMileage(null);

      if (!isCarValueTracker) {
        VALUATIONS_GA_TAGS.CONFIRM_MILEAGE_MODAL_CANCELLED({ mileage: confirmedMileage?.mileage, vrm });
      }
    },
  }), [updateMileage, vrm]);

  useEffect(() => {
    if (enquiryID) {
      getValuationStatus(enquiryID).then((data) => {
        const dpEnquiryStatus = data?.state?.slug;
        setEnquiryStatus(dpEnquiryStatus);
        assignOptimizelyWebData('dpEnquiryStatus', dpEnquiryStatus ?? null);
      }).catch((err) => {
        timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException(new Error(err)));
      });
    } else {
      assignOptimizelyWebData('dpEnquiryStatus', null);
    }
  }, [enquiryID]);

  return {
    confirmedMileage,
    confirmMileageHelpers,
    onFormSubmit,
    onNotMyCarClick,
  };
};
